/**
 * A row's status in a table — brief part 1 §1 (pkgC2), `docs/adr/9028`: a mono SYMBOL and a
 * WORD on one line, at 600, and never the colour alone (§12.6).
 *
 *   ●  open       to decide              ink
 *   ✓  good       approved               status-good-strong
 *   ✕  critical   rejected               status-critical-strong
 *   ◐  modified   adjusted               ink
 *   —  neutral    not eligible, history  mark-neutral (the symbol) · ink-label (the word)
 *
 * The shape tells the five apart without the hue, so a planner who cannot see the colour, or
 * prints the page, still reads it. The symbols are not copy: the same glyph in every locale,
 * like `GHOST_MARK`, and hidden from assistive technology, which hears the word.
 *
 * ONE DEPARTURE FROM THE BRIEF, stated: the brief paints «Non idonea» in #8a847b
 * (`mark-neutral`). As text that is 3.71:1 on white and 3.32:1 on the emphasised column — under
 * WCAG AA's 4.5:1 — so the grey stays on the SYMBOL, where 3:1 is the bar and it clears it,
 * and the word takes `ink-label` (#5b5550, 7.34:1 on white; #aba79b in dark). The row still
 * reads grey; the word is readable.
 *
 * `StatusIndicator` (display.tsx) is still the status of a THING — a feed, a rule — with its
 * four severity levels. This is the status of a ROW in a decision table.
 */

import { useTranslation } from 'react-i18next';

import type { ICUValues } from './display';

export const STATUS_MARKS = ['open', 'good', 'critical', 'modified', 'neutral'] as const;
export type StatusMarkKind = (typeof STATUS_MARKS)[number];

/** The symbol per kind — data, not copy. */
export const STATUS_GLYPH: Readonly<Record<StatusMarkKind, string>> = {
  open: '●',
  good: '✓',
  critical: '✕',
  modified: '◐',
  neutral: '—',
};

/** The symbol's colour. */
const GLYPH_TONE: Readonly<Record<StatusMarkKind, string>> = {
  open: 'text-ink-primary',
  good: 'text-status-good-strong',
  critical: 'text-status-critical-strong',
  modified: 'text-ink-primary',
  neutral: 'text-mark-neutral',
};

/** The word's colour — the symbol's, except where that grey cannot carry text (see above). */
const WORD_TONE: Readonly<Record<StatusMarkKind, string>> = {
  ...GLYPH_TONE,
  neutral: 'text-ink-label',
};

export interface StatusMarkProps {
  readonly mark: StatusMarkKind;
  /** REQUIRED: the word is the carrier; the symbol and the colour only repeat it. */
  readonly labelKey: string;
  readonly values?: ICUValues;
}

export function StatusMark({ mark, labelKey, values }: StatusMarkProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap font-semibold ${WORD_TONE[mark]}`} data-status-mark={mark}>
      <span aria-hidden className={`font-numeric ${GLYPH_TONE[mark]}`}>{STATUS_GLYPH[mark]}</span>
      <span>{t(labelKey, values ?? {})}</span>
    </span>
  );
}
